import React, { memo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityTypesSupportingRoutes, Route } from "@mapmycustomers/shared/types/entity";

import AddOrCreateButton from "@app/component/preview/components/AddOrCreateButton";
import styles from "@app/component/preview/components/Relationships/Relationships.module.scss";
import Section from "@app/component/preview/components/Section";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import EditRoutesPanel from "../EditRoutesPanel";

interface Props {
  entityType: EntityTypesSupportingRoutes;
  hasLocation?: boolean;
  isReadOnlyMember: boolean;
  onAdd: () => void;
  onRemove?: (route: Route) => void;
  removeRouteLoadingIds?: Route["id"][];
  routes: Route[];
  routesLoading?: boolean;
}

const Routes: React.FC<Props> = ({
  entityType,
  hasLocation,
  isReadOnlyMember,
  onAdd,
  onRemove,
  removeRouteLoadingIds = [],
  routes,
  routesLoading = false,
}) => {
  const intl = useIntl();

  return (
    <Section
      action={
        <AddOrCreateButton
          disabled={!hasLocation || isReadOnlyMember}
          label={intl.formatMessage({
            id: "entityPreview.relationships.routes.addButton",
            defaultMessage: "Add to Route",
            description: "Title of a button to add a route relationship",
          })}
          loading={routesLoading}
          onClick={onAdd}
          tooltip={
            hasLocation
              ? intl.formatMessage({
                  id: "entityPreview.relationships.routes.addButton.tooltip",
                  defaultMessage: "Add to route",
                  description: "Title of an Add to route button on a Record pane",
                })
              : intl.formatMessage({
                  id: "entityPreview.relationships.routes.addButton.disabled.tooltip",
                  defaultMessage:
                    "Unable to add due to invalid address. Please update the record’s location to add it to a route.",
                  description: "Tooltip for disabled Add to route button on a Record pane",
                })
          }
        />
      }
      title={intl.formatMessage(
        {
          id: "entityPreview.relationships.routes.title",
          defaultMessage: "Routes ({count})",
          description: "Routes section title of company record pane",
        },
        { count: routes.length }
      )}
      titleClassName={styles.sectionTitle}
    >
      <div className={styles.container}>
        <EditRoutesPanel
          entityType={entityType}
          onRemoveRoute={onRemove}
          removeLoadingIds={removeRouteLoadingIds}
          routes={routes}
        />
        {routes.length !== 1 ? null : (
          // a dummy div to force single route be in a first column
          <div />
        )}
      </div>
    </Section>
  );
};

const mapStateToProps = (state: RootState) => ({
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

export default connect(mapStateToProps)(memo(Routes));
